import { useMemo, useRef, useState } from "react";
import { useData } from "@/context/DataContext";
import { useAuth } from "@/context/AuthContext";
import TaskRow from "@/components/TaskRow";
import TaskModal from "@/components/TaskModal";
import AmmTabs from "@/components/AmmTabs";
import { Button } from "@/components/ui/button";
import { Plus, Wallet, Camera } from "lucide-react";
import { ADMIN_SECTION_CLIENT_ID } from "@/lib/roles";
import { fileToResizedDataUrl } from "@/lib/image";
import { toast } from "sonner";

export default function AmmTask() {
  const { tasks, clients, updateClient } = useData();
  const { user } = useAuth();
  const [taskOpen, setTaskOpen] = useState(false);
  const iconInputRef = useRef(null);

  const isAdmin = user && user.role === "admin";
  const adminClient = clients.find((c) => c.id === ADMIN_SECTION_CLIENT_ID);
  const adminIcon = adminClient?.avatar_url || null;

  const onIconFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) return toast.error("Immagine troppo grande (max 4MB)");
    try {
      const url = await fileToResizedDataUrl(file, 320, 0.85);
      await updateClient(ADMIN_SECTION_CLIENT_ID, { avatar_url: url });
      toast.success("Icona aggiornata");
    } catch {
      toast.error("Errore caricamento immagine");
    }
  };

  const adminTasks = useMemo(
    () =>
      tasks.filter(
        (t) => t.client_id === ADMIN_SECTION_CLIENT_ID && t.status !== "completato",
      ),
    [tasks],
  );
  const allCount = useMemo(
    () => tasks.filter((t) => t.client_id === ADMIN_SECTION_CLIENT_ID).length,
    [tasks],
  );
  const completed = allCount - adminTasks.length;

  return (
    <div className="space-y-6 tp-fade-in">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => isAdmin && iconInputRef.current?.click()}
          data-testid="admin-icon-picker"
          disabled={!isAdmin}
          title={isAdmin ? "Cambia icona Amministrazione" : undefined}
          className="relative h-11 w-11 rounded-2xl overflow-hidden flex items-center justify-center shadow-lg shadow-fuchsia-600/30 shrink-0 group focus:outline-none focus:ring-2 focus:ring-fuchsia-500/60"
          style={{
            backgroundImage: adminIcon ? `url(${adminIcon})` : undefined,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          {!adminIcon && (
            <span className="absolute inset-0 tp-brand-gradient flex items-center justify-center">
              <Wallet size={20} className="text-white" />
            </span>
          )}
          {isAdmin && (
            <span className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/45 transition-colors opacity-0 group-hover:opacity-100">
              <Camera size={16} className="text-white" />
            </span>
          )}
        </button>
        <input
          ref={iconInputRef}
          data-testid="admin-icon-input"
          type="file"
          accept="image/*"
          className="hidden"
          onChange={onIconFile}
        />
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Interna</div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-white">
            Amministrazione
          </h1>
        </div>
      </div>

      <AmmTabs />

      <section className="tp-glass rounded-3xl overflow-hidden">
        <div className="px-5 sm:px-6 py-4 border-b border-white/5 flex items-center gap-3">
          <h2 className="text-base font-semibold text-white">Task Amministrazione</h2>
          <span className="ml-auto text-xs text-zinc-500 tabular-nums" data-testid="admin-count">
            {adminTasks.length} attive · {completed} completate
          </span>
          <Button
            data-testid="add-admin-task-button"
            onClick={() => setTaskOpen(true)}
            className="tp-brand-btn h-9 rounded-full px-3"
          >
            <Plus size={14} className="mr-1.5" /> Nuova task
          </Button>
        </div>
        <div className="px-2 sm:px-3 py-2">
          {adminTasks.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-zinc-500 italic">
              Nessuna task di amministrazione. Aggiungi la prima con "Nuova task".
            </div>
          ) : (
            <>
              <div className="hidden sm:grid grid-cols-[minmax(0,1fr)_140px_130px_130px_120px_auto] gap-3 px-4 pb-2 text-[10px] uppercase tracking-widest text-zinc-500">
                <div>Task</div>
                <div>Team</div>
                <div>Scadenza</div>
                <div>Urgente</div>
                <div>Stato</div>
                <div />
              </div>
              <div className="rounded-2xl overflow-hidden">
                {adminTasks.map((t) => (
                  <TaskRow key={t.id} task={t} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <TaskModal
        open={taskOpen}
        onOpenChange={setTaskOpen}
        initialClientId={ADMIN_SECTION_CLIENT_ID}
      />
    </div>
  );
}
